import React from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainAnimalsResultWeightGroupSearchQuery } from '../api/HealthSystemApi';
import { Slider } from './ui/slider';
import { LabeledCheckbox } from './ui/labeled-checkbox';

interface IProps {
  disabled?: boolean;
  onChange: (option: HealthSystemDomainAnimalsResultWeightGroupSearchQuery) => void;
  value?: HealthSystemDomainAnimalsResultWeightGroupSearchQuery;
}

const STEP = 0.05;

const bounds = {
  birthWeight: { min: 0, max: 4 },
  weanedWeight: { min: 3, max: 15 },
  tenWeeksWeight: { min: 15, max: 30 }
};

const defaultRanges = {
  birthWeight: [0, 4],
  weanedWeight: [3, 15],
  tenWeeksWeight: [15, 30]
};

// Snap to the slider step and strip floating point artifacts (e.g. 8.200000000000001 -> 8.2).
// STEP (0.05) has 2 decimals, so rounding to 2 decimals yields a clean value.
const roundToStep = (n: number) => Math.round(Math.round(n / STEP) * STEP * 100) / 100;

const WeightGroupFilter = (props: IProps) => {
  const { disabled, onChange, value } = props;
  const { t } = useTranslation();

  // Sync local state when parent provides or updates `value`
  React.useEffect(() => {
    if (!value) {
      return;
    }

    setRanges((prev) => ({
      birthWeight: value.birth?.length === 2 ? value.birth : prev.birthWeight,
      weanedWeight: value.weaned?.length === 2 ? value.weaned : prev.weanedWeight,
      tenWeeksWeight: value.tenWeeks?.length === 2 ? value.tenWeeks : prev.tenWeeksWeight
    }));

    setEnabledFilters({
      birthWeight: !!value.birth?.length,
      weanedWeight: !!value.weaned?.length,
      tenWeeksWeight: !!value.tenWeeks?.length
    });
  }, [value]);

  // Track filter enabled/disabled state
  const [enabledFilters, setEnabledFilters] = React.useState({
    birthWeight: false,
    weanedWeight: false,
    tenWeeksWeight: false
  });

  // Track the slider values (UI)
  const [ranges, setRanges] = React.useState(defaultRanges);

  // Update enabled state and trigger filter update to backend.
  // Note: onChange must be called from the event handler, never from inside a
  // setState updater (those run during render and would update the parent mid-render).
  const handleCheckboxChange = (key: keyof typeof enabledFilters) => {
    const updated = { ...enabledFilters, [key]: !enabledFilters[key] };
    setEnabledFilters(updated);
    // Immediately send new values to backend when toggled
    handleChange(ranges, updated);
  };

  // Update slider values, trigger filter update
  const handleRangeChange = (key: keyof typeof ranges, newValue: number[]) => {
    const updated = { ...ranges, [key]: newValue.map(roundToStep) };
    setRanges(updated);
    handleChange(updated, enabledFilters);
  };

  // Precise value entry via the From/To inputs. `index` 0 = from, 1 = to.
  const handleInputChange = (key: keyof typeof ranges, index: 0 | 1, raw: string) => {
    const { min, max } = bounds[key];
    const parsed = Number.parseFloat(raw);

    const next = [...ranges[key]] as number[];
    // Keep empty/invalid input from blowing away the value; fall back to the bound
    const fallback = index === 0 ? min : max;
    let value = Number.isNaN(parsed) ? fallback : roundToStep(parsed);

    // Clamp to the allowed bounds
    value = Math.min(Math.max(value, min), max);

    // Keep from <= to (the inputs allow equal/close values on purpose)
    if (index === 0) {
      value = Math.min(value, next[1]);
    } else {
      value = Math.max(value, next[0]);
    }

    next[index] = value;
    const updated = { ...ranges, [key]: next };
    setRanges(updated);
    handleChange(updated, enabledFilters);
  };

  // Only send enabled ranges to backend, disabled are empty arrays
  const handleChange = (customRanges = ranges, customEnabled = enabledFilters) => {
    const command: HealthSystemDomainAnimalsResultWeightGroupSearchQuery = {
      birth: customEnabled.birthWeight ? customRanges.birthWeight : [],
      weaned: customEnabled.weanedWeight ? customRanges.weanedWeight : [],
      tenWeeks: customEnabled.tenWeeksWeight ? customRanges.tenWeeksWeight : []
    };
    onChange(command);
  };

  const filters: {
    key: keyof typeof ranges;
    label: string;
  }[] = [
    { key: 'birthWeight', label: 'birth-weight' },
    { key: 'weanedWeight', label: 'weaned-weight' },
    { key: 'tenWeeksWeight', label: 'ten-weeks-weight' }
  ];

  return (
    <Grid container spacing={1} sx={{ justifyContent: 'space-between' }}>
      {filters.map(({ key, label }) => {
        const { min, max } = bounds[key];
        const isDisabled = disabled || !enabledFilters[key];

        return (
          <Grid key={key} size={{ xs: 12, sm: 4 }} sx={{ px: 1, pb: 1 }}>
            <div style={{ minHeight: 40 }}>
              <LabeledCheckbox
                checked={enabledFilters[key]}
                onCheckedChange={() => handleCheckboxChange(key)}
                label={t(`f-search-${label}`)}
                disabled={disabled}
              />
            </div>

            <div className="mt-1 px-1">
              <Slider
                value={ranges[key]}
                disabled={isDisabled}
                onValueChange={(newValue) => handleRangeChange(key, newValue)}
                min={min}
                max={max}
                step={STEP}
                minStepsBetweenThumbs={1}
                aria-label={t(`f-search-${label}`)}
              />

              <div className={`mt-1 flex justify-between text-sm ${isDisabled ? 'text-gray-300' : 'text-gray-500'}`}>
                <span>{min}Kg</span>
                <span>{max}Kg</span>
              </div>
            </div>

            <div className="mt-3 flex items-end gap-3 px-1">
              <label className={`min-w-0 flex-1 text-sm ${isDisabled ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('f-com-from')}
                <input
                  type="number"
                  inputMode="decimal"
                  disabled={isDisabled}
                  value={ranges[key][0]}
                  min={min}
                  max={ranges[key][1]}
                  step={STEP}
                  onChange={(e) => handleInputChange(key, 0, e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="mt-1 block w-full min-w-0 box-border rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                />
              </label>
              <label className={`min-w-0 flex-1 text-sm ${isDisabled ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('f-com-to')}
                <input
                  type="number"
                  inputMode="decimal"
                  disabled={isDisabled}
                  value={ranges[key][1]}
                  min={ranges[key][0]}
                  max={max}
                  step={STEP}
                  onChange={(e) => handleInputChange(key, 1, e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="mt-1 block w-full min-w-0 box-border rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                />
              </label>
            </div>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default WeightGroupFilter;
